<template>
  <div
    class="flex flex-col h-full"
    @mouseup="resizeLeftStop"
    @mouseleave="resizeLeftStop"
    @mousemove="resizeLeftMove"
    ref="grid"
  >
    <div class="w-full flex flex-row h-full grow overflow-y-hidden">
      <div
        v-if="$slots.left"
        ref="left"
        class="h-full flex-1 overflow-y-auto bg-primary relative border-r border-secondary overflow-x-visible shadow-xl hide-scrollbar"
        :style="leftStyle"
        @scroll="syncScroll('left')"
      >
        <div class="relative">
          <div
            class="absolute top-0 right-0 h-full w-2 cursor-ew-resize z-10"
            @mousedown="resizeLeftStart"
          ></div>
          <slot name="left"></slot>
        </div>
      </div>
      <div
        v-if="$slots.center"
        ref="center"
        class="overflow-x-auto overflow-y-auto overscroll-none h-full bg-primary no-scrollbar"
        @scroll="syncScroll('center')"
        :style="centerStyle"
      >
        <slot name="center"></slot>
      </div>
    </div>

    <div class="border-t border-secondary">
      <slot name="footer" />
    </div>
  </div>
</template>

<script setup>
// Import Vue Utilites
import { ref, computed, watchEffect } from "vue";

// Emits
const emit = defineEmits(["update:leftWidth"]);

// Define Props
const props = defineProps({
  showColumns: {
    type: Array,
    required: true,
  },
  leftWidth: {
    type: Number,
    required: true,
    validator: (value) => value > 0 && value < 100,
  },
  minLeftWidth: {
    type: Number,
    required: true,
    validator: (value) => value > 1 && value < 99,
  },
  maxLeftWidth: {
    type: Number,
    required: true,
    validator: (value) => value > 1 && value < 99,
  },
});

// Refs
const grid = ref(null);
const left = ref(null);
const center = ref(null);
const leftWidth = ref(props.leftWidth);
const clientX = ref(0);

// Computed
const leftStyle = computed(() => ({
  width: `${leftWidth.value}%`,
}));

const centerStyle = computed(() => ({
  width: `${100 - leftWidth.value}%`,
}));

// Functions
const syncScroll = (side) => {
  if (side === "left") {
    center.value.scrollTop = left.value.scrollTop;
  }

  if (side === "center") {
    left.value.scrollTop = center.value.scrollTop;
  }
};

const resizeLeftStart = (e) => {
  e.preventDefault();
  clientX.value = e.clientX;
};

const resizeLeftStop = () => {
  clientX.value = 0;
};

const resizeLeftMove = (e) => {
  if (!clientX.value) return;

  const movement = e.clientX - clientX.value;
  const step = grid.value.clientWidth / 100;

  if (Math.abs(movement) > step) {
    const delta = movement > 0 ? 1.1 : -1.1;
    const newSize = leftWidth.value + delta;

    if (newSize >= props.minLeftWidth && newSize <= props.maxLeftWidth) {
      leftWidth.value = newSize;
      clientX.value = e.clientX;
      emit("update:leftWidth", newSize);
    }
  }
};

watchEffect(() => {
  leftWidth.value = props.showColumns.length === 0 ? 100 : props.leftWidth;
});
</script>
